import React, { useState, useEffect, useCallback } from 'react';
import { C, R, Card, SectionLabel, Badge } from '../ui/kit';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Database,
  Eye,
  FlaskConical,
  Gauge,
  History,
  KeyRound,
  Layers,
  LockKeyhole,
  Play,

  ShieldAlert,
  Sliders,
} from 'lucide-react';
import './UltimatePricingV2.css';

// ─────────────────────────────────────────────────────────────────────────────
// ULT-PRICING-V2 — Admin-Dashboard. READ-FIRST: alles zeigt zuerst den Zustand.
// Schreibende Aktionen (Modus, Draft/Aktivieren, Rollback, Kill, Proposals) sind
// bewusst mit Bestätigung versehen. Der Kurvensimulator ist strikt read-only.
// ─────────────────────────────────────────────────────────────────────────────
// Ein Betriebsweg: off→shadow→test→live. Test und live wirken global; historische Altmodi bleiben
// ausschließlich für unveränderliche Ledgerzeilen lesbar.
const MODE_COLORS = { off: C.muted, shadow: '#2563eb', test: '#d97706', live: '#16a34a', test_canary: '#d97706', on: '#16a34a' };
const HEAT = { grey: '#6b7280', blue: '#3b82f6', green: '#22c55e', yellow: '#eab308', orange: '#f97316', red: '#ef4444' };
const fmt = (v, d = 2) => (v == null || isNaN(v) ? '—' : Number(v).toFixed(d));
// ULT-PRICING-V2-001R2 E: JEDE Mutation verlangt einen NICHT-LEEREN Grund (auditiert). null = abgebrochen.
function promptReason(label) {
  const r = window.prompt(`${label}\n\nGrund (Pflicht):`, '');
  if (r == null) return null;
  const t = r.trim();
  if (!t) { toast.error('Ein Grund ist Pflicht.'); return null; }
  return t;
}
const apiErr = (e) => e.response?.data?.message || e.response?.data?.error || 'Fehlgeschlagen';

// ─────────────────────────────────────────────────────────────────────────────
// R4R1-7: EINE Quelle der Wahrheit für die im Cockpit dargestellten Vertragsparameter. PARAM_KEYS MUSS
// deckungsgleich mit Object.keys(contract.defaults()) sein — der Coverage-Test
// (UltimatePricingV2.param-coverage.test.js) erzwingt das → kein stiller Drift; eine 18. Default OHNE Zeile
// lässt den Test rot werden. Reihenfolge = Anzeigereihenfolge. curveAnchors ist der 17. Vertragsparameter.
export const PARAM_KEYS = [
  'qMin', 'dMin', 'radiusKm', 'confirmSec', 'exitConfirmSec', 'alphaUp', 'alphaDown',
  'floor', 'cap', 'combinedCap', 'roundStep', 'pushBase', 'pushSlope', 'surgeEntryRho',
  'curveAnchors', 'snapshotIntervalMs', 'staleMs',
];
const fmtAnchors = (a) => (Array.isArray(a) && a.length
  ? a.map((x) => `${Number(x[0]).toFixed(2)}→${x[1]}`).join('  ·  ') : '—');
const PARAM_META = {
  qMin:               { label: 'Q-Minimum',          unit: 'Anfragen', effect: 'Unter diesem Wert IMMER Ziel 0,75', fix: false },
  dMin:               { label: 'D-Minimum',          unit: 'Fahrer',   effect: 'Unter diesem Wert IMMER Ziel 0,75', fix: false },
  radiusKm:           { label: 'Radius',             unit: 'km',       effect: 'Messkreis vom Pickup/Zonenmittelpunkt', fix: true },
  confirmSec:         { label: 'Bestätigungszeit',   unit: 's',        effect: 'kontinuierlich bis Surge bestätigt', fix: false },
  exitConfirmSec:     { label: 'Exit-Bestätigung',   unit: 's',        effect: 'Surge-Ausstiegs-Hysterese (nur ρ-seitig)', fix: false },
  alphaUp:            { label: 'alphaUp',             unit: '',         effect: 'EMA-Glättung aufwärts', fix: false },
  alphaDown:          { label: 'alphaDown',           unit: '',         effect: 'EMA-Glättung abwärts', fix: false },
  floor:              { label: 'Marktfloor',         unit: '',         effect: 'Pushboden (nur Marktfaktor)', fix: true },
  cap:                { label: 'Marktcap',           unit: '',         effect: 'Höchster Marktfaktor', fix: false },
  combinedCap:        { label: 'Combined-Cap',       unit: '',         effect: 'market × behavior ≤ dieser Wert', fix: false },
  roundStep:          { label: 'Round-Step',         unit: '',         effect: 'Rundung des angewandten Faktors', fix: false },
  pushBase:           { label: 'Push-Basis',         unit: '',         effect: 'Push-Basiswert (Basis + slope·ρ, geklemmt [Basis,1,00])', fix: false },
  pushSlope:          { label: 'Push-Slope',         unit: '',         effect: 'Push-Steigung über ρ', fix: false },
  surgeEntryRho:      { label: 'Surge-Einstieg ρ',   unit: 'ρ',        effect: 'Surge-Schwelle (Nachfrage/Angebot)', fix: false },
  curveAnchors:       { label: 'Kurvenanker',        unit: 'ρ→Faktor', effect: 'Piecewise-lineare Surge-Zielkurve (X fix, Y tunbar)', fix: false, fmt: fmtAnchors },
  snapshotIntervalMs: { label: 'Snapshot-Intervall', unit: 'ms',       effect: 'Matching-Tick (Producer)', fix: false },
  staleMs:            { label: 'Stale-Grenze',       unit: 'ms',       effect: 'Snapshot-Frische-Horizont: expires_at = generated_at + max(staleMs, 2·Proof-Budget + Intervall); Producer-TTL = max(3·Intervall, staleMs, Proof-Budget + Intervall)', fix: false },
};

const TABS = [
  ['system', 'Systemzustand', Activity, 'Modi, Gates & Notaus'],
  ['zones', 'Live-Zonen', Gauge, 'Snapshot & Heatmap'],
  ['simulate', 'Kurvensimulator', FlaskConical, 'Read-only Szenarien'],
  ['config', 'Konfiguration', Sliders, 'Parameter & Revisionen'],
  ['ledger', 'Quote-Suche', History, 'Auditierte Herleitung'],
  ['learning', 'Learning-Proposals', Layers, 'Evidenz & Freigabe'],
];

export default function UltimatePricingV2() {
  const [tab, setTab] = useState('system');
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadState = useCallback(async () => {
    try { const r = await api.get('/admin/ultimate-v2/state'); setState(r.data); }
    // R4R3-C: fail-closed — ein fehlgeschlagener/abgelehnter Refresh VERWIRFT den Zustand, statt veraltete
    // Owner-Controls + sensible Ledgerdaten stehen zu lassen.
    catch (e) { setState(null); toast.error('Systemzustand konnte nicht geladen werden'); }
  }, []);
  useEffect(() => { loadState(); const t = setInterval(loadState, 5000); return () => clearInterval(t); }, [loadState]);

  // R4R2-3/R4R3-C/D: EINE Quelle der Fähigkeiten. Gültig NUR auf frisch+erfolgreich geladenem Zustand
  // (Fehler ⇒ state=null ⇒ false; pending-initial ⇒ state=null ⇒ false; fehlendes Feld/Downgrade ⇒ false).
  const canManage = state?.can_manage === true;   // engine.manage ODER settings.manage/'*'
  const canOwner = state?.can_owner === true;      // NUR settings.manage/'*'
  const canApplyPricing = state?.can_apply_pricing === true; // Berechtigung + nicht delegierbare Owner-Identitaet
  const canApplyProposals = state?.can_apply_proposals === true; // Proposal-Entscheidung + Apply/Rollback, serverseitig exakt gespiegelt
  const snapshotHealth = state?.snapshot_health || {};
  const accessLabel = !state ? 'Status ausstehend' : canApplyPricing ? 'Owner Control' : canManage ? 'Manager Control' : 'Read-only';
  // R4R3-C: verliert der Aufrufer can_manage bei offenem (owner-gated) Ledger-Tab → sofort sicherer Tab.
  useEffect(() => { if (tab === 'ledger' && !canManage) setTab('system'); }, [tab, canManage]);

  return (
    <div className="uv2-root">
      <PageHeader
        title="Ultimate Pricing V2"
        description="Kanonischer Marktsnapshot · deterministische Policy · versionierte Config · vollständiges Quote-Audit. Alles ruht, bis ein berechtigter Admin bewusst handelt."
        actions={(
          <>
            {state && <Badge color={MODE_COLORS[state.market_mode] || C.muted}>{(state.market_mode || 'off').toUpperCase()}</Badge>}
            {state && state.kill && state.kill.all && <Badge color="#ef4444">KILL-SWITCH AKTIV</Badge>}
            <div className={`uv2-safety-seal ${state?.kill?.all ? 'is-danger' : ''}`}>
              {state?.kill?.all ? <ShieldAlert size={18} /> : <LockKeyhole size={18} />}
              <span><small>Safety State</small><strong>{state?.kill?.all ? 'V2 Notaus aktiv' : 'Kontrollierter Betrieb'}</strong></span>
            </div>
          </>
        )}
      />

      <section className="uv2-command-strip" aria-label="V2 Betriebsübersicht">
        <div className="uv2-command-tile">
          <span className={`uv2-command-icon mode-${state?.market_mode || 'off'}`}><Activity size={15} /></span>
          <span><small>Marktmodus</small><strong>{state ? (state.market_mode || 'off').toUpperCase() : 'WIRD GELADEN'}</strong></span>
          <i>{state?.raw_market_mode && state.raw_market_mode !== state.market_mode ? `roh ${state.raw_market_mode}` : 'effektiver Zustand'}</i>
        </div>
        <div className="uv2-command-tile">
          <span className={`uv2-command-icon ${snapshotHealth.ok ? 'is-ready' : 'is-warning'}`}>{snapshotHealth.ok ? <CheckCircle2 size={15} /> : <Gauge size={15} />}</span>
          <span><small>Snapshot-Gate</small><strong>{!state ? 'WIRD GEPRÜFT' : snapshotHealth.ok ? 'FRISCH + PASSEND' : 'NICHT BEREIT'}</strong></span>
          <i>{snapshotHealth.zones != null ? `${snapshotHealth.zones} Zonen` : 'generationsweit'}</i>
        </div>
        <div className="uv2-command-tile">
          <span className={`uv2-command-icon ${state?.key_ready ? 'is-ready' : 'is-warning'}`}><KeyRound size={15} /></span>
          <span><small>Quote-Ledger</small><strong>{!state ? 'WIRD GEPRÜFT' : state.key_ready ? 'SCHLÜSSEL BEREIT' : 'SCHLÜSSEL FEHLT'}</strong></span>
          <i>Pflicht für test und live</i>
        </div>
        <div className="uv2-command-tile">
          <span className={`uv2-command-icon ${canOwner ? 'is-owner' : ''}`}>{canManage ? <Database size={15} /> : <Eye size={15} />}</span>
          <span><small>Zugriff</small><strong>{accessLabel.toUpperCase()}</strong></span>
          <i>{canOwner ? 'settings.manage' : canManage ? 'engine.manage' : 'engine.view'}</i>
        </div>
      </section>

      <div role="tablist" aria-label="Ultimate Pricing V2 Arbeitsbereiche" className="uv2-workspaces">
        {/* R4-H3: der owner-gated Ledger-Tab (GET /ledger = engine.manage) wird reinen Viewern ausgeblendet →
            kein 403-Sturm aus dem Auto-Fetch. Übrige Mutationen sind zusätzlich serverseitig RBAC-geschützt. */}
        {TABS.filter(([id]) => id !== 'ledger' || canManage).map(([id, label, Icon, description]) => (
          <button key={id} role="tab" aria-selected={tab === id} aria-controls={`uv2-panel-${id}`} onClick={() => setTab(id)} className={tab === id ? 'is-active' : ''}>
            <span className="uv2-workspace-icon"><Icon size={15} /></span>
            <span><strong>{label}</strong><small>{description}</small></span>
          </button>
        ))}
      </div>

      <div id={`uv2-panel-${tab}`} role="tabpanel" className={`uv2-panel uv2-panel-${tab}`}>
        {tab === 'system' && <SystemPanel state={state} reload={loadState} busy={busy} setBusy={setBusy} mgr={canManage} canOwner={canOwner} canApplyPricing={canApplyPricing} />}
        {tab === 'zones' && <ZonesPanel />}
        {tab === 'simulate' && <SimulatePanel />}
        {tab === 'config' && <ConfigPanel reload={loadState} canManage={canManage} canApplyPricing={canApplyPricing} />}
        {/* R4R3-C: Ledger-Panel wird bei Verlust von can_manage SOFORT unmountet (nicht nur der Tab-Button versteckt)
            → geladene sensible Ledger-Daten verschwinden, kein weiterer owner-gated /ledger-Fetch. */}
        {tab === 'ledger' && canManage && <LedgerPanel />}
        {tab === 'learning' && <LearningPanel state={state} mgr={canManage} canApplyProposals={canApplyProposals} />}
      </div>
    </div>
  );
}

const kv = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.line}`, fontSize: 13 };

// ── 1) SYSTEMZUSTAND ──────────────────────────────────────────────────────────
function SystemPanel({ state, reload, busy, setBusy, mgr, canOwner, canApplyPricing }) {
  if (!state) return <Card><SkeletonLine /></Card>;
  // R4R3-C: mgr ist jetzt Prop vom Top-Level (status-aware, fail-closed); canOwner trennt Owner-only-Hebel.
  const expectedFor = (which) => which === 'market' ? state.raw_market_mode : which === 'behavior' ? state.raw_behavior_mode : state[which + '_mode'];
  const setMode = async (which, value) => {
    if (value === expectedFor(which)) return;
    const reason = promptReason(`Modus „${which}" → „${value}"`);
    if (reason == null) return;
    setBusy(true);
    try { await api.post('/admin/ultimate-v2/mode', { which, value, reason, expected: expectedFor(which) }); toast.success('Modus gesetzt'); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setKill = async (which, on) => {
    const reason = promptReason(on ? `Notaus „${which}" AKTIVIEREN (V2 → sofort Ultimate V1)` : `Notaus „${which}" AUFHEBEN (Owner-only; Markt startet wieder bei off)`);
    if (reason == null) return;
    setBusy(true);
    try { await api.post('/admin/ultimate-v2/kill', { which, on, reason, expected: state.kill[which] }); toast.success('Kill-Switch gesetzt'); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setHeatmap = async (on) => {
    const reason = promptReason(`Fahrer-Heatmap ${on ? 'EIN' : 'AUS'}schalten`);
    if (reason == null) return;
    setBusy(true);
    // R4-H7: Heatmap hat jetzt Pflicht-CAS — expected = roher Flag-Wert (nicht der kill-maskierte heatmap_enabled).
    try { await api.post('/admin/ultimate-v2/heatmap-enabled', { on, reason, expected: state.heatmap_enabled_raw }); toast.success('Heatmap-Schalter gesetzt'); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  // R3-F: V2-eigene Behavior-Signalquelle, Behavior-full-Unlock (Owner-only).
  const setBehaviorSignals = async (on) => {
    const reason = promptReason(`V2-Behavior-Signalquelle ${on ? 'EIN' : 'AUS'}schalten`);
    if (reason == null) return; setBusy(true);
    try { await api.post('/admin/ultimate-v2/behavior-signals', { on, reason, expected: state.behavior_signals_ready }); toast.success('Signalquelle gesetzt'); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setBehaviorFull = async (on) => {
    const reason = promptReason(`Behavior „full" ${on ? 'FREISCHALTEN (Owner/Legal)' : 'SPERREN'}`);
    if (reason == null) return; setBusy(true);
    try { await api.post('/admin/ultimate-v2/behavior-full-unlock', { on, reason, expected: state.behavior_full_unlocked }); toast.success('Behavior-full gesetzt'); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  // Testkunden-/Canary-Kohorten sind entfernt. `test` und `live` sind globale Marktmodi und nutzen
  // denselben Preis- und Ledgerpfad.
  const revokeV2 = async () => {
    if (!window.confirm('NOT-WIDERRUF: ALLE offenen V2-Quotes sofort entwerten? Kunden müssen neu anfragen (→ dann V1). Nur im Notfall.')) return;
    const reason = promptReason('Not-Widerruf offener V2-Quotes');
    if (reason == null) return;
    setBusy(true);
    try { const r = await api.post('/admin/ultimate-v2/quotes/revoke-v2', { reason }); toast.success(`${r.data.revoked} V2-Quote(s) widerrufen`); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const sh = state.snapshot_health || {};
  const ORDER = ['off', 'shadow', 'test', 'live'];
  const stepOk = (from, to) => { const a = ORDER.indexOf(from), b = ORDER.indexOf(to); return b <= a || b === a + 1; };
  return (
    <div className="uv2-system-grid">
      {!mgr && <Card className="uv2-card uv2-alert uv2-alert-readonly" style={{ marginBottom: 12, borderColor: C.line }}><div style={{ color: C.muted, fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><AlertTriangle size={15} /> Nur-Lese-Ansicht (engine.view): Systemzustand sichtbar, Schalter erfordern engine.manage/settings.manage.</div></Card>}
      {state.active_config_valid === false && <Card className="uv2-card uv2-alert uv2-alert-danger" style={{ marginBottom: 12, borderColor: '#fecaca' }}><div style={{ color: '#ef4444', fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><AlertTriangle size={15} /> Aktive Config ist ungültig (Params/Hash/Schema) — V2 bleibt fail-closed auf V1. Bitte eine gültige Revision aktivieren.</div></Card>}
      <Card className="uv2-card uv2-market-card">
        <SectionLabel>Marktmodus (effektiv / roh) — Aktivierungsfolge off→shadow→test→live</SectionLabel>
        {/* R4R1-4: Marktmodus-Pills nur für Owner sichtbar; Viewer sehen den effektiven Modus als Text unten. */}
        {mgr && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0 4px' }}>
            {ORDER.map((m) => {
              const allowed = m === state.raw_market_mode || stepOk(state.raw_market_mode, m);
              const permitted = !['test', 'live'].includes(m) || canApplyPricing;
              const title = !allowed ? 'Kein Sprung — Stufe für Stufe' : !permitted ? 'Geschuetzter Owner fuer Preiswirkung erforderlich' : '';
              return <button key={m} aria-label={`Marktmodus ${m}`} disabled={busy || !allowed || !permitted} title={title}
                onClick={() => setMode('market', m)} style={{ ...pillBtn(state.raw_market_mode === m, MODE_COLORS[m]), opacity: allowed && permitted ? 1 : 0.4 }}>{m}</button>;
            })}
          </div>
        )}
        <div style={{ fontSize: 12, color: C.muted }}>effektiv: <b style={{ color: MODE_COLORS[state.market_mode] }}>{state.market_mode}</b>{state.market_mode !== state.raw_market_mode && ` (roh: ${state.raw_market_mode} — durch Kill-Switch überschrieben)`}.</div>
        <ul style={{ fontSize: 13, color: C.muted, margin: '6px 0 0', paddingLeft: 16 }}>
          <li><b>shadow</b>: rechnet alles mit, verändert den Kundenpreis NICHT (V1-neutral).</li>
          <li><b>test</b>: V2 gilt für alle Fahrten. Derselbe Candidate-Preisweg wie live; Kennzeichnung nur für kontrollierte Freigabe und Audit.</li>
          <li><b>live</b>: V2 gilt ebenfalls für alle Fahrten. Der Wechsel test→live ändert keinen Kunden- oder Fahrtcode.</li>
          <li><b>Abhängigkeiten</b>: test/live brauchen denselben dedizierten Ledger-Schlüssel; der erste Wechsel in den preiswirksamen Pfad braucht frische, config-passende Snapshots. Behavior „full" braucht den Owner/Legal-Unlock.</li>
          <li><b>Learning</b>: off erzeugt und übernimmt nichts; shadow erzeugt nur Vorschläge; live erlaubt ausschließlich den manuellen Owner-Apply. Auto-Apply bleibt ausgeschlossen.</li>
        </ul>
      </Card>

      <Card className="uv2-card uv2-secondary-card" style={{ marginTop: 12 }}>
        <SectionLabel>Nebenmodi — Verhalten, Telemetrie, Learning, Heatmap</SectionLabel>
        <ModeRow label="Behavior-Pricing" which="behavior" values={['off', 'discount_only', 'full']} current={state.raw_behavior_mode}
          effect={`Verhaltensfaktor STACKT auf Window-Shopper (ersetzt nie). „full" braucht den Owner/Legal-Unlock (unten). Effektiv: ${state.behavior_mode}. Signalquelle: ${state.behavior_signals_ready ? 'aktiv' : 'INAKTIV → Behavior bleibt neutral (behavior_signals_inactive)'}.`} onSet={setMode} busy={busy} mgr={mgr}
          canSet={(value) => value === 'off' || canApplyPricing} />
        <div style={{ margin: '6px 0 8px', paddingLeft: 8, borderLeft: `2px solid ${C.line}` }}>
          <div style={{ fontSize: 12, marginBottom: 3 }}>V2-Behavior-Signalquelle: <b style={{ color: state.behavior_signals_ready ? '#16a34a' : '#d97706' }}>{state.behavior_signals_ready ? 'aktiv' : 'inaktiv'}</b> <span style={{ color: C.faint }}>({state.behavior_signals_reason})</span></div>
          {mgr && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button disabled={busy || (!state.behavior_signals_ready && !canApplyPricing)}
                title={!state.behavior_signals_ready && !canApplyPricing ? 'Geschuetzter Owner zum Einschalten erforderlich' : undefined}
                onClick={() => setBehaviorSignals(!state.behavior_signals_ready)} style={pillBtn(state.behavior_signals_ready, C.brand)}>{state.behavior_signals_ready ? 'Signalquelle aus' : 'Signalquelle an'}</button>
              {/* R4R3-D: „full"-Unlock ist ein Owner-Akt (settings.manage/'*') — für reine Manager nicht bedienbar. */}
              {canOwner && <button disabled={busy || (!state.behavior_full_unlocked && !canApplyPricing)}
                title={!state.behavior_full_unlocked && !canApplyPricing ? 'Geschuetzter Owner zum Freischalten erforderlich' : undefined}
                onClick={() => setBehaviorFull(!state.behavior_full_unlocked)} style={pillBtn(state.behavior_full_unlocked, '#7c3aed')}>{state.behavior_full_unlocked ? 'full gesperrt (Owner)' : 'full freischalten (Owner)'}</button>}
            </div>
          )}
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Behavior ist NICHT mehr still vom Legacy-„Wir sind live" abhängig — die V2-Signalquelle ist ein eigener Owner-Schalter. „full"-Unlock ist Owner-only (settings.manage).</div>
        </div>
        <ModeRow label="Telemetrie" which="telemetry" values={['off', 'collect']} current={state.telemetry_mode}
          effect={`„collect" schreibt den Quote-Ledger. Marktmodus test/live erfordert denselben dedizierten LEDGER_HMAC_KEY — Schlüssel bereit: ${state.key_ready ? 'ja' : 'NEIN (V2-Aktivierung blockiert)'}.`} onSet={setMode} busy={busy} mgr={mgr} />
        <ModeRow label="Learning" which="learning" values={['off', 'shadow', 'live']} current={state.learning_mode}
          effect={'off: keine Vorschläge/kein Apply · shadow: Vorschläge ohne Apply · live: manueller Owner-Apply nach Freigabe. Niemals Auto-Apply; Evidenz-Gates und stabile Richtung bleiben Pflicht.'} onSet={setMode} busy={busy} mgr={mgr}
          canSet={(value) => value !== 'live' || canApplyPricing} />
        <div style={{ margin: '8px 0' }}>
          <div style={{ fontSize: 12, color: C.muted }}>Dedizierter Ledger-Schlüssel (für „test" und „live"): <b style={{ color: state.key_ready ? '#16a34a' : '#d97706' }}>{state.key_ready ? 'bereit' : 'fehlt'}</b>. Ohne Schlüssel bleibt der globale V2-Preisweg API-seitig blockiert.</div>
        </div>
        <div style={{ margin: '8px 0' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Fahrer-Heatmap</div>
          {/* R4R1-4: Owner schalten; Viewer sehen nur den Status als Badge. */}
          {mgr ? <button disabled={busy} onClick={() => setHeatmap(!state.heatmap_enabled)} style={pillBtn(state.heatmap_enabled, '#16a34a')}>{state.heatmap_enabled ? 'aktiv — ausschalten' : 'aus — einschalten'}</button>
               : <Badge color={state.heatmap_enabled ? '#16a34a' : C.muted}>{state.heatmap_enabled ? 'aktiv' : 'aus'}</Badge>}
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Zeigt Fahrern die V2-Marktkarte (Push/Surge-Zonen). Rein informativ, ändert keinen Preis.</div>
        </div>
      </Card>

      <Card className="uv2-card uv2-runtime-card" style={{ marginTop: 12 }}>
        <SectionLabel>Producer & Snapshot</SectionLabel>
        <div style={kv}><span>Producer</span><Badge color={state.producer_active ? '#16a34a' : C.muted}>{state.producer_active ? 'running' : 'dormant'}</Badge></div>
        <div style={kv}><span>Lease-Halter / Fence</span><span>{state.lease ? `${state.lease.holder} · #${state.lease.fence}` : '—'}</span></div>
        <div style={kv}><span>Snapshot-Alter</span><span>{sh.missing ? 'kein Snapshot' : `${Math.round((sh.age_ms || 0) / 1000)} s ${sh.stale ? '(stale)' : ''}`}</span></div>
        <div style={{ ...kv, borderBottom: 'none' }}><span>Aktive Config-Revision</span><span>{state.active_revision ? `#${state.active_revision.rev} · ${(state.active_revision.hash || '').slice(0, 10)}` : 'Standard'}</span></div>
      </Card>

      <Card className="uv2-card uv2-readiness-card" style={{ marginTop: 12 }}>
        <SectionLabel>Aktivität & Modellversion</SectionLabel>
        {/* Technische Bereitschaft ist getrennt von tatsächlich verwertbarer Evidenz. */}
        {(() => {
          const missing = [];
          if (!['test', 'live'].includes(state.market_mode)) missing.push('Marktmodus test oder live');
          if (state.telemetry_mode !== 'collect') missing.push('Telemetrie collect');
          if (state.key_ready !== true) missing.push('Ledger-Schlüssel');
          return (
            <>
              <div style={kv}>
                <span>V2-Evidenzfluss (technische Voraussetzungen)</span>
                <Badge color={missing.length === 0 ? '#16a34a' : C.muted}>{missing.length === 0 ? 'erfüllt' : 'nicht bereit'}</Badge>
              </div>
              <div style={{ fontSize: 13, color: C.muted, margin: '2px 0 6px' }}>
                {missing.length === 0
                  ? 'Preiswirksamer Marktmodus + Telemetrie collect + Ledger-Schlüssel — lernberechtigte Fahrten können Evidenz liefern. Ob sie bereits verwertbar ist, sagen ausschließlich die Evidenz-Gates im Tab „Learning-Proposals".'
                  : `Es fehlt: ${missing.join(', ')}. Evidenz kann erst entstehen, wenn alle drei zusammenkommen.`}
              </div>
            </>
          );
        })()}
        <div style={{ fontSize: 13, color: C.muted, padding: 8, background: C.brandTint, borderRadius: R.md, margin: '4px 0 8px' }}>
          <b>Ablauf:</b> Learning <b>shadow</b> erzeugt Vorschläge ohne Wirkung · Owner prüft und genehmigt · Learning <b>live</b> erlaubt den manuellen Apply · Producer erzeugt danach passende Snapshots. Kein Auto-Apply.
        </div>
        {state.last_activation && <div style={{ ...kv, borderBottom: 'none' }}><span>Letzte Aktion</span><span>{state.last_activation.action} · {state.last_activation.actor || '—'}</span></div>}
        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Modellversion: {state.model_version}</div>
      </Card>

      <Card className="uv2-card uv2-snapshot-card" style={{ marginTop: 12 }}>
        <SectionLabel>Snapshot-Gesundheit (identisch zum test/live-Aktivierungsgate — generationsweit)</SectionLabel>
        {sh.missing ? <div style={{ color: '#d97706', fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><AlertTriangle size={15} /> Kein Snapshot vorhanden — V2 fällt fail-cheap auf 0,75; test/live blockiert.</div>
          : <>
            <div style={kv}><span>Status</span><Badge color={sh.ok ? '#16a34a' : '#ef4444'}>{sh.ok ? 'frisch + passend (test/live möglich)' : `blockiert test/live: ${sh.reason || 'stale'}`}</Badge></div>
            <div style={kv}><span>Zonen (jüngste Generation)</span><span>{sh.zones ?? '—'}</span></div>
            <div style={kv}><span>Alter</span><span>{sh.age_ms != null ? `${Math.round(sh.age_ms / 1000)} s` : '—'}</span></div>
            {!sh.ok && <>
              <div style={{ ...kv, color: '#ef4444' }}><span>Fehlerhafte Zone</span><span>{sh.failing_zone || '—'}</span></div>
              <div style={kv}><span>Zonen-Revision / Hash</span><span>{(sh.config_revision ? String(sh.config_revision).slice(0, 8) : 'std')} / {(sh.config_hash ? String(sh.config_hash).slice(0, 10) : '—')}</span></div>
              <div style={{ ...kv, borderBottom: 'none' }}><span>Erwartet Revision / Hash</span><span>{(sh.expected_revision ? String(sh.expected_revision).slice(0, 8) : 'std')} / {(sh.expected_hash ? String(sh.expected_hash).slice(0, 10) : '—')}</span></div>
            </>}
          </>}
      </Card>

      {/* R4R1-4: der gesamte Notfallschalter (Kill + Not-Widerruf) ist owner-only — Viewer sehen den Kill-Status
          über die Kopf-Badge „KILL-SWITCH AKTIV", aber keine bedienbaren Schalter. */}
      {mgr && (
      <Card className="uv2-card uv2-emergency-card" style={{ marginTop: 12, borderColor: '#fecaca' }}>
        <SectionLabel><span style={{ color: '#ef4444', display: 'inline-flex', gap: 6, alignItems: 'center' }}><ShieldAlert size={15} /> Notfallschalter</span></SectionLabel>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {['all', 'behavior', 'learning', 'heatmap'].map((w) => {
            // R4R3-D: einen Kill ANZIEHEN darf der Manager (Notaus); AUFHEBEN (RELEASE) ist Owner-only —
            // deckt sich mit dem Server-Hinweis „Kill AUFHEBEN ist Owner-only".
            const on = state.kill[w];
            const canToggle = on ? canApplyPricing : true;
            return (
              <button key={w} disabled={busy || !canToggle} title={on && !canToggle ? 'Kill aufheben ist Owner-only (settings.manage)' : undefined}
                onClick={() => setKill(w, !on)} style={{ ...pillBtn(on, '#ef4444'), opacity: canToggle ? 1 : 0.5, cursor: canToggle ? 'pointer' : 'not-allowed' }}>
                {on ? `⛔ ${w} AN` : `${w}`}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 12, color: C.muted, margin: '8px 0 4px' }}>„all" = kompletter V2-Notaus → sofort exakt Ultimate V1. Standard-Kill: NEUE Quotes sofort V1, bereits gezeigte Quotes bleiben bis zu ihrer TTL gültig. Kill AUFHEBEN ist Owner-only und startet den Markt bei off.</div>
        {/* R4R3-D: Not-Widerruf entwertet bereits gezeigte Quotes → Owner-only (settings.manage/'*'), nicht Manager. */}
        {canOwner && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.line}` }}>
          <button disabled={busy} onClick={revokeV2} style={{ ...pillBtn(false, '#b91c1c'), border: '1px solid #b91c1c' }}>Not-Widerruf offener V2-Quotes (Owner-only)</button>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Entwertet zusätzlich die bereits gezeigten, offenen V2-Quotes sofort (Requote nötig). Getrennt vom Standard-Kill.</div>
        </div>
        )}
      </Card>
      )}
    </div>
  );
}
function ModeRow({ label, which, values, current, effect, onSet, busy, mgr = true, canSet = () => true }) {
  return (
    <div style={{ margin: '8px 0' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label} <span style={{ color: C.faint, fontWeight: 400 }}>· {current}</span></div>
      {/* R4R1-4: reine Viewer (engine.view) sehen den Status, aber KEINE Umschalt-Buttons. */}
      {mgr && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {values.map((v) => {
            const permitted = canSet(v);
            return <button key={v} aria-label={`${label} Modus ${v}`} disabled={busy || !permitted}
              title={!permitted ? 'Geschuetzter Owner fuer Preiswirkung erforderlich' : undefined}
              onClick={() => onSet(which, v)} style={{ ...pillBtn(current === v, MODE_COLORS[v] || C.brand), opacity: permitted ? 1 : 0.5 }}>{v}</button>;
          })}
        </div>
      )}
      {effect && <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{effect}</div>}
    </div>
  );
}
const pillBtn = (active, color) => ({ padding: '6px 12px', borderRadius: R.pill, border: `1px solid ${active ? color : C.line}`, background: active ? color : C.card, color: active ? '#fff' : C.text, cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400 });

// ── 2) LIVE-ZONEN + HEATMAP ───────────────────────────────────────────────────
function ZonesPanel() {
  const [data, setData] = useState(null);
  const [heat, setHeat] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/admin/ultimate-v2/live-zones'); setData(r.data); } catch { /* */ }
    try { const h = await api.get('/admin/ultimate-v2/heatmap'); setHeat(h.data); } catch { /* */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  if (!data) return <Card><SkeletonLine /></Card>;
  if (!data.zones.length) return (
    <div className="uv2-zone-stack">
      <Card className="uv2-card uv2-empty-card"><div style={{ color: C.muted, fontSize: 13 }}>Noch keine gemessenen Zonen (kein Live-Verkehr).</div></Card>
      <HeatmapCard heat={heat} />
    </div>
  );
  return (
    <div className="uv2-zone-stack">
    <Card className="uv2-card uv2-table-card uv2-zones-card">
      <SectionLabel>Live-Zonen (kanonischer Snapshot)</SectionLabel>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
          <thead><tr style={{ textAlign: 'left', color: C.muted }}>
            {['Zone', 'Q', 'D', 'ρ', 'Ziel', 'Effektiv', 'Peak', 'Bestät. in', 'Alter', 'Rev'].map((h) => <th key={h} style={{ padding: '4px 8px' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {data.zones.map((z) => (
              <tr key={z.zone} style={{ borderTop: `1px solid ${C.line}`, background: z.stale ? '#fafafa' : 'transparent' }}>
                <td style={{ padding: '4px 8px' }}>{z.zone}</td>
                <td style={{ padding: '4px 8px' }}>{z.q}</td>
                <td style={{ padding: '4px 8px' }}>{z.d}</td>
                <td style={{ padding: '4px 8px' }}>{fmt(z.rho)}</td>
                <td style={{ padding: '4px 8px' }}>{fmt(z.target)}</td>
                <td style={{ padding: '4px 8px', fontWeight: 600 }}>{fmt(z.effective)}</td>
                <td style={{ padding: '4px 8px' }}><Badge color={z.peak === 'surge' ? '#ef4444' : z.peak === 'push' ? '#3b82f6' : C.muted}>{z.peak}</Badge></td>
                <td style={{ padding: '4px 8px' }}>{z.confirm_remaining_ms != null ? `${Math.ceil(z.confirm_remaining_ms / 1000)} s` : '—'}</td>
                <td style={{ padding: '4px 8px', color: z.stale ? '#d97706' : C.muted }}>{Math.round(z.age_ms / 1000)} s{z.stale ? ' ⚠' : ''}</td>
                <td style={{ padding: '4px 8px', color: C.faint }}>{z.config_revision ? z.config_revision.slice(0, 6) : 'std'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>„Surge durch Mindestfahrer-Gate unterdrückt", falls Q&lt;5 oder D&lt;3 → Ziel bleibt 0,75.</div>
    </Card>
    <HeatmapCard heat={heat} />
    </div>
  );
}
// Echte V2-Heatmap: farbige Zellen aus dem kanonischen Snapshot (dieselben Werte wie Ledger/Pricing).
function HeatmapCard({ heat }) {
  return (
    <Card className="uv2-card uv2-heatmap-card" style={{ marginTop: 12 }}>
      <SectionLabel>V2-Heatmap (kanonischer Snapshot)</SectionLabel>
      {!heat || !heat.cells || !heat.cells.length ? <div style={{ color: C.muted, fontSize: 13 }}>Noch keine Zellen.</div> : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
            {heat.cells.map((c) => (
              <div key={c.zone} title={`${c.zone}\nQ=${c.q} D=${c.d} ρ=${fmt(c.rho)}\neffektiv=${fmt(c.effective)}${c.stale ? ' (stale)' : ''}`}
                style={{ width: 46, height: 46, borderRadius: R.md, background: HEAT[c.color] || HEAT.grey, opacity: c.stale ? 0.45 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                {fmt(c.effective, 2)}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: C.muted }}>
            {(heat.legend || []).map((l) => <span key={l.color} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><span style={{ width: 12, height: 12, borderRadius: 3, background: HEAT[l.color] || l.color, display: 'inline-block' }} /> {l.label}</span>)}
          </div>
        </>
      )}
    </Card>
  );
}

// ── 3) KURVENSIMULATOR (read-only) ────────────────────────────────────────────
function SimulatePanel() {
  const [res, setRes] = useState(null);
  const run = useCallback(async () => {
    try { const r = await api.post('/admin/ultimate-v2/simulate', { scenarios: [{ d: 3 }, { d: 5 }, { d: 10 }, { d: 15 }] }); setRes(r.data); }
    catch { toast.error('Simulation fehlgeschlagen'); }
  }, []);
  useEffect(() => { run(); }, [run]);
  return (
    <Card className="uv2-card uv2-simulator-card">
      <SectionLabel><span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><FlaskConical size={15} /> Kurvensimulator — read-only</span></SectionLabel>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>3/5/10/15-Fahrer-Szenarien. Zeigt Push/Neutral/Peak-Ziel und den geglätteten Verlauf. Speichert NIE die aktive Config.</div>
      <button onClick={run} style={{ ...pillBtn(false, C.brand), marginBottom: 10 }}><Play size={13} /> Neu berechnen</button>
      {res && res.scenarios.map((s) => (
        <div key={s.d} style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>D = {s.d} Fahrer</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ textAlign: 'left', color: C.muted }}>{['Q', 'ρ', 'Ziel (bestätigt)', 'Vorstufe', 'geglättet', 'Gründe'].map((h) => <th key={h} style={{ padding: '3px 8px' }}>{h}</th>)}</tr></thead>
              <tbody>
                {s.points.map((p, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.line}` }}>
                    <td style={{ padding: '3px 8px' }}>{p.q}</td>
                    <td style={{ padding: '3px 8px' }}>{fmt(p.rho)}</td>
                    <td style={{ padding: '3px 8px', fontWeight: 600 }}>{fmt(p.target_confirmed)}</td>
                    <td style={{ padding: '3px 8px' }}>{fmt(p.push_or_neutral)}</td>
                    <td style={{ padding: '3px 8px', color: C.brand }}>{fmt(p.smoothed)}</td>
                    <td style={{ padding: '3px 8px', color: C.faint, fontSize: 12 }}>{(p.reasons_confirmed || []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </Card>
  );
}

// ── 4) KONFIGURATION ──────────────────────────────────────────────────────────
function ConfigPanel({ reload, canManage = false, canApplyPricing = false }) {   // Capabilities fehlen ⇒ fail-closed
  const [data, setData] = useState(null);
  const load = useCallback(async () => { try { const r = await api.get('/admin/ultimate-v2/config'); setData(r.data); } catch { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  const activate = async (id) => {
    const reason = promptReason('Revision aktivieren');
    if (reason == null) return;
    try { await api.post('/admin/ultimate-v2/config/activate', { revision_id: id, reason, expected_active_id: data.active.revisionId }); toast.success('Aktiviert'); load(); reload(); }
    catch (e) { toast.error(e.response?.data?.error === 'stale_revision' ? 'Konflikt: Revision hat sich geändert (409)' : e.response?.data?.error === 'unsafe_active_mode' ? 'Erst Markt-Modus auf shadow/off zurückstufen (frische Snapshots)' : apiErr(e)); }
  };
  const rollback = async () => {
    const reason = promptReason('Auf die vorherige Revision zurückrollen');
    if (reason == null) return;
    // ULT-PRICING-V2-001R2 C5: Rollback MIT Mandatory-CAS (expected_active_id) — sonst 428.
    try { await api.post('/admin/ultimate-v2/config/rollback', { reason, expected_active_id: data.active.revisionId }); toast.success('Rollback ausgeführt'); load(); reload(); }
    catch (e) { toast.error(e.response?.data?.error === 'stale_revision' ? 'Konflikt: aktive Revision hat sich geändert (409)' : apiErr(e)); }
  };
  if (!data) return <Card><SkeletonLine /></Card>;
  const p = data.active.params;
  // R4R1-7: ALLE 17 Vertragsparameter (16 Skalare + curveAnchors) aus EINER Quelle (PARAM_KEYS/PARAM_META).
  // Der Coverage-Test erzwingt PARAM_KEYS === Object.keys(contract.defaults()). Spalte „fix" markiert die
  // serverseitig gesperrten Vertragskonstanten (floor 0,75 · Radius 4,5 km).
  const REGLER = PARAM_KEYS.map((k) => {
    const m = PARAM_META[k];
    const cur = m.fmt ? m.fmt(p[k]) : p[k];
    const def = m.fmt ? m.fmt(data.defaults[k]) : data.defaults[k];
    return [m.label, cur, def, m.unit, m.effect, m.fix];
  });
  return (
    <div className="uv2-config-stack">
      <Card className="uv2-card uv2-table-card uv2-parameters-card">
        <SectionLabel>Aktive Parameter{data.active.rev ? ` (Revision #${data.active.rev})` : ' (Standard)'}</SectionLabel>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
            <thead><tr style={{ textAlign: 'left', color: C.muted }}>{['Regler', 'Aktuell', 'Default', 'Einheit', 'Wirkung', 'Änderbar'].map((h) => <th key={h} style={{ padding: '4px 8px' }}>{h}</th>)}</tr></thead>
            <tbody>
              {REGLER.map(([label, cur, def, unit, effect, readOnly]) => (
                <tr key={label} style={{ borderTop: `1px solid ${C.line}` }}>
                  <td style={{ padding: '4px 8px', fontWeight: 600 }}>{label}</td>
                  <td style={{ padding: '4px 8px' }}>{cur}{(unit && typeof cur === 'number') ? ` ${unit}` : ''}</td>
                  <td style={{ padding: '4px 8px', color: C.faint }}>{def}</td>
                  <td style={{ padding: '4px 8px', color: C.faint }}>{unit || '—'}</td>
                  <td style={{ padding: '4px 8px', color: C.muted, fontSize: 13 }}>{effect}</td>
                  <td style={{ padding: '4px 8px', color: readOnly ? '#d97706' : C.faint, fontSize: 13 }}>{readOnly ? 'Vertragskonstante (fix)' : 'tunbar'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: C.muted, padding: 8, background: C.brandTint, borderRadius: R.md }}>
          Workflow: <b>Draft → Simulation → Shadow → Owner-Freigabe → Aktivierung → Rollback</b>. Aktivierung ist atomar und auditiert; eine Aktivierung direkt in einen scharfen Modus (test/live) ist gesperrt (erst auf shadow zurück → frische Snapshots).
        </div>
      </Card>

      {/* R4-G1: key={data.active.hash} → der Editor re-seedet bei Wechsel der aktiven Revision. Sonst hielte er
          alte Feldwerte und würde sie still unter dem NEUEN Eltern-Hash speichern (CAS bestände, falsche Revision). */}
      {canManage && <DraftEditor key={data.active.hash} data={data} onDone={() => { load(); reload(); }} />}

      <Card className="uv2-card uv2-revisions-card" style={{ marginTop: 12 }}>
        <SectionLabel>Revisionen</SectionLabel>
        {data.revisions.length ? data.revisions.map((r) => (
          <div key={r.id} style={{ ...kv, alignItems: 'center' }}>
            <span>#{r.rev} · {r.params_hash?.slice(0, 10)} · <span style={{ color: C.faint }}>{r.reason || '—'}</span>{data.active.revisionId === r.id && <Badge color="#16a34a">aktiv</Badge>}</span>
            {canApplyPricing && data.active.revisionId !== r.id && <button onClick={() => activate(r.id)} style={pillBtn(false, C.brand)}>aktivieren</button>}
          </div>
        )) : <div style={{ fontSize: 13, color: C.muted }}>Keine Revisionen — Standard-Parameter aktiv.</div>}
        {canApplyPricing && data.active.revisionId && <button onClick={rollback} style={{ ...pillBtn(false, '#d97706'), marginTop: 10 }}>↩ Rollback auf vorherige Revision</button>}
      </Card>

      <Card className="uv2-card uv2-audit-card" style={{ marginTop: 12 }}>
        <SectionLabel>Änderungs-Audit</SectionLabel>
        {data.audit.slice(0, 20).map((a) => (
          <div key={a.id} style={{ fontSize: 12, color: C.muted, padding: '3px 0', borderBottom: `1px solid ${C.line}` }}>
            <b style={{ color: C.text }}>{a.action}</b> · {a.actor || '—'} · {a.at} {a.reason ? `· „${a.reason}"` : ''}
          </div>
        ))}
      </Card>
    </div>
  );
}

const inputS = { width: '100%', boxSizing: 'border-box', padding: '5px 7px', border: `1px solid ${C.line}`, borderRadius: R.md, fontSize: 12, marginTop: 2 };
// R4R2-6: EINE Quelle der editierbaren (server-tunbaren) Skalar-Parameter des Draft-Editors.
// Jeder Vertrags-Default AUSSER den zwei Vertragskonstanten (floor 0,75 · radiusKm 4,5 — validateParams
// erzwingt exakte Werte) und dem separat als Y-Anker editierten Array curveAnchors bekommt ein Eingabefeld.
// alphaUp/alphaDown teilen sich EIN Feld (Vertrag: alphaUp == alphaDown). Reihenfolge = Anzeigereihenfolge.
const DRAFT_FIELDS = [
  ['qMin', 'Q-Minimum'], ['dMin', 'D-Minimum'], ['confirmSec', 'Bestätigung s'], ['exitConfirmSec', 'Exit-Best. s'],
  ['cap', 'Marktcap'], ['combinedCap', 'Combined-Cap'], ['roundStep', 'Round-Step'], ['pushBase', 'Push-Basis'],
  ['pushSlope', 'Push-Slope'], ['surgeEntryRho', 'Surge-Einstieg ρ'],
  ['snapshotIntervalMs', 'Snapshot-Intervall ms'], ['staleMs', 'Stale-Grenze ms'],
];
// Coverage-Wächter (R4R2-6): server-tunbare Skalar-Keys mit Editor-Feld = alle DRAFT_FIELDS PLUS die zwei
// gekoppelten alpha-Keys (ein Feld). MUSS === PARAM_KEYS \ {floor, radiusKm, curveAnchors} sein (Test erzwingt es).
export const EDITABLE_KEYS = [...DRAFT_FIELDS.map(([k]) => k), 'alphaUp', 'alphaDown'];
// Config-Draft-Editor: editierbare Parameter, Vorher/Nachher-Diff, read-only Simulation des Drafts, dann
// „Draft anlegen" (neue unveränderliche Revision, Mandatory-CAS gegen die aktive, Pflicht-Grund).
export function DraftEditor({ data, onDone }) {   // R4-G: benannter Export für den fokussierten Re-Seed-Test
  const base = data.active.params;
  const FIELDS = DRAFT_FIELDS;   // R4R2-6: module-scope Single-Source (Coverage-Test koppelt EDITABLE_KEYS daran)
  const [f, setF] = useState(() => { const o = {}; FIELDS.forEach(([k]) => { o[k] = base[k]; }); o.alpha = base.alphaUp; o.anchors = (base.curveAnchors || []).map((a) => a[1]); return o; });
  const [open, setOpen] = useState(false);
  const [sim, setSim] = useState(null);
  const draftParams = () => {
    const next = JSON.parse(JSON.stringify(base));
    FIELDS.forEach(([k]) => { next[k] = Number(f[k]); });
    next.alphaUp = Number(f.alpha); next.alphaDown = Number(f.alpha);
    // R4R3-B: der erste Kurvenanker-X ist an surgeEntryRho gekoppelt (Server: curveAnchors[0][0]===surgeEntryRho).
    // Beim Ändern von surgeEntryRho MUSS Anker-0-X mitgeführt werden, sonst weist validateParams JEDEN ρ-Wechsel
    // ab. Übrige Anker-X bleiben fix (a[0]); alle Y stammen aus den Anker-Y-Feldern. Die Monotonie prüft der Server.
    next.curveAnchors = (base.curveAnchors || []).map((a, i) => [i === 0 ? Number(f.surgeEntryRho) : a[0], Number(f.anchors[i])]);
    return next;
  };
  const diff = () => {
    const d = draftParams(); const out = [];
    const cmp = (k, cur, nv) => { if (Number(cur) !== Number(nv)) out.push([k, cur, nv]); };
    FIELDS.forEach(([k]) => cmp(k, base[k], d[k]));
    cmp('alphaUp', base.alphaUp, d.alphaUp); cmp('alphaDown', base.alphaDown, d.alphaDown);
    (base.curveAnchors || []).forEach((a, i) => cmp('anchorY[' + i + ']', a[1], d.curveAnchors[i][1]));
    return out;
  };
  const simulate = async () => { try { const r = await api.post('/admin/ultimate-v2/simulate', { params: draftParams(), scenarios: [{ d: 3 }, { d: 5 }, { d: 10 }] }); setSim(r.data); } catch (e) { toast.error(e.response?.data?.detail ? 'Ungültig: ' + e.response.data.detail : apiErr(e)); } };   // G3: gleiche Ablehnung wie Speichern
  const createDraft = async () => {
    const changes = diff();
    if (!changes.length) { toast.error('Keine Änderung gegenüber der aktiven Config.'); return; }
    const reason = promptReason(`Config-Draft anlegen (${changes.length} Änderung(en))`);
    if (reason == null) return;
    try { const r = await api.post('/admin/ultimate-v2/config/draft', { params: draftParams(), reason, expected_parent_hash: data.active.hash }); toast.success('Draft #' + r.data.rev + ' angelegt'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail ? 'Ungültig: ' + e.response.data.detail : e.response?.data?.error === 'stale_revision' ? 'Aktive Revision hat sich geändert (409)' : apiErr(e)); }
  };
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setAnchor = (i, v) => setF((s) => { const a = [...s.anchors]; a[i] = v; return { ...s, anchors: a }; });
  const changes = diff();
  return (
    <Card className="uv2-card uv2-draft-card" style={{ marginTop: 12 }}>
      <SectionLabel>Neuer Config-Draft — Vorher/Nachher-Diff & Simulation</SectionLabel>
      {!open ? <button onClick={() => setOpen(true)} style={pillBtn(false, C.brand)}>Draft-Editor öffnen</button> : (
        <>
          <div style={{ fontSize: 13, color: C.muted, margin: '4px 0 8px' }}>Vertragskonstanten (floor 0,75 · Radius 4,5 km) sind bewusst NICHT editierbar. Ungültige Kombinationen weist der Server ab.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 8 }}>
            {FIELDS.map(([k, label]) => (<label key={k} style={{ fontSize: 12 }}>{label}<input type="number" step="any" value={f[k]} onChange={(e) => set(k, e.target.value)} style={inputS} /></label>))}
            <label style={{ fontSize: 12 }}>alpha (up=down)<input type="number" step="any" value={f.alpha} onChange={(e) => set('alpha', e.target.value)} style={inputS} /></label>
          </div>
          <div style={{ marginTop: 8, fontSize: 12 }}>Kurvenanker Y (X fix): {f.anchors.map((y, i) => <input key={i} type="number" step="any" value={y} onChange={(e) => setAnchor(i, e.target.value)} style={{ ...inputS, width: 64, display: 'inline-block', marginRight: 6 }} />)}</div>
          <div style={{ marginTop: 10 }}>
            <SectionLabel>Diff (aktiv → Draft)</SectionLabel>
            {changes.length ? changes.map(([k, o, n]) => <div key={k} style={{ ...kv, fontSize: 12 }}><span>{k}</span><span><span style={{ color: C.faint }}>{String(o)}</span> → <b style={{ color: C.brand }}>{String(n)}</b></span></div>) : <div style={{ fontSize: 12, color: C.muted }}>Keine Änderung.</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={simulate} style={pillBtn(false, C.brand)}><Play size={12} /> Draft simulieren</button>
            <button onClick={createDraft} style={pillBtn(false, '#16a34a')}>Draft anlegen</button>
          </div>
          {sim && <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>Simulation (Draft): {sim.scenarios.map((s) => `D${s.d}: ${s.points.map((pt) => fmt(pt.applied_confirmed)).join('/')}`).join('  ·  ')}</div>}
        </>
      )}
    </Card>
  );
}

// ── 5) QUOTE-SUCHE ────────────────────────────────────────────────────────────
function LedgerPanel() {
  const [q, setQ] = useState('');
  const [cust, setCust] = useState('');
  const [mode, setMode] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [rows, setRows] = useState(null);
  // G5: Suche nach quote_id ODER Kunde/Modus/Zeitfenster (since..until, datetime-local → ms).
  const toMs = (v) => (v ? new Date(v).getTime() : undefined);
  const search = async () => {
    try {
      const params = q ? { quote_id: q } : { limit: 50, customer: cust || undefined, mode: mode || undefined, since: toMs(since), until: toMs(until) };
      const r = await api.get('/admin/ultimate-v2/ledger', { params });
      setRows(q ? (r.data.quote ? [r.data.quote] : []) : r.data.quotes);
    } catch { toast.error('Suche fehlgeschlagen'); }
  };
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);
  const inp = { padding: '6px 9px', border: `1px solid ${C.line}`, borderRadius: R.md, fontSize: 13 };
  return (
    <Card className="uv2-card uv2-ledger-card">
      <SectionLabel>Quote-Suche (vollständige Herleitung) — quote_id ODER Kunde/Modus/Zeitfenster</SectionLabel>
      <div style={{ display: 'flex', gap: 8, margin: '8px 0', flexWrap: 'wrap' }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="quote_id (leer = Filter unten)" style={{ ...inp, flex: '1 1 200px' }} />
        <input value={cust} onChange={(e) => setCust(e.target.value)} placeholder="Kunde (Klartext-ID)" style={inp} />
        {/* Nur der aktive globale Modusvertrag ist als Filter sichtbar. Unveränderliche Altzeilen
            bleiben über die ungefilterte Suche lesbar, ohne stillgelegte Kohortennamen anzuzeigen. */}
        <select value={mode} onChange={(e) => setMode(e.target.value)} style={inp}><option value="">Modus: alle</option>{['off', 'shadow', 'test', 'live'].map((m) => <option key={m} value={m}>{m}</option>)}</select>
        <input type="datetime-local" value={since} onChange={(e) => setSince(e.target.value)} title="von" style={inp} />
        <input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} title="bis" style={inp} />
        <button onClick={search} style={pillBtn(false, C.brand)}>Suchen</button>
      </div>
      {rows && rows.map((r) => (
        <div key={r.quote_id} style={{ border: `1px solid ${C.line}`, borderRadius: R.md, padding: 10, marginTop: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{r.quote_id} <Badge color={MODE_COLORS[r.mode]}>{r.mode_is_historical ? 'Altbestand' : r.mode}</Badge>{r.historical_cohort ? <Badge color={C.muted}>Historischer Datensatz</Badge> : null}{r.mode_is_historical ? <Badge color={C.muted}>Historischer Modus</Badge> : null}{r.telemetry ? <Badge color="#2563eb">telemetry</Badge> : null}</div>
          <Grid pairs={[
            ['Kunde (pseudonym)', r.customer_ref], ['Zone', r.pickup_zone], ['Route-FP', r.route_fp], ['Stadt', r.city], ['Kontext', r.ctx || r.payment_ctx],
            ['Q', r.q], ['D', r.d], ['ρ', fmt(r.rho)], ['Peak', r.peak_state], ['km', fmt(r.distance_km)], ['min', fmt(r.duration_min)], ['Klasse', r.ride_type],
            ['Ziel-Faktor', fmt(r.target_factor)], ['Effektiv', fmt(r.effective_factor)], ['Behavior', fmt(r.behavior_factor)], ['Behavior-Grund', r.behavior_reason],
            ['Kombiniert (v/n Cap)', `${fmt(r.combined_before_cap)} / ${fmt(r.combined_after_cap)}`], ['classF/cityF', `${fmt(r.class_factor)} / ${fmt(r.city_factor)}`], ['P0', fmt(r.base_p0)],
            ['V1-Faktor', fmt(r.v1_factor)], ['V1-Preis', fmt(r.v1_price)], ['V2-Engine-Preis', fmt(r.v2_engine_price)], ['Finaler Preis', fmt(r.final_price)],
            ['Snapshot', r.snapshot_id?.slice(0, 8)], ['Revision', r.config_revision ? r.config_revision.slice(0, 8) : 'std'], ['Modell', r.model_version],
            ['Promo', r.promo_json], ['Erstellt', r.created_at], ['Läuft ab', r.expires_at], ['Gründe', r.reasons],
          ]} />
        </div>
      ))}
      {rows && !rows.length && <div style={{ color: C.muted, fontSize: 13 }}>Keine Einträge (Ledger füllt sich erst, wenn der Markt-Modus ≠ off ist).</div>}
    </Card>
  );
}
function Grid({ pairs }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '2px 12px' }}>
    {pairs.map(([k, v]) => <div key={k} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${C.line}`, padding: '2px 0' }}><span style={{ color: C.muted }}>{k}</span><span style={{ fontWeight: 500 }}>{v == null || v === '' ? '—' : String(v)}</span></div>)}
  </div>;
}

// ── 6) LEARNING-PROPOSALS ─────────────────────────────────────────────────────
function LearningPanel({ state, mgr, canApplyProposals }) {   // Proposal-Entscheidung: nicht delegierbarer Owner, fail-closed
  const [data, setData] = useState(null);
  const load = useCallback(async () => { try { const r = await api.get('/admin/ultimate-v2/proposals'); setData(r.data); } catch { /* */ } }, []);
  useEffect(() => { load(); }, [load]);
  // Apply verlangt serverseitig einen Pflicht-Grund (Owner-only). Rollback/Generate ebenso mit Grund.
  const act = async (id, path, label) => {
    const reason = promptReason(label);
    if (reason == null) return;
    try { await api.post(`/admin/ultimate-v2/proposals/${id}/${path}`, { reason }); toast.success('OK'); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const decide = async (id, decision) => {
    const reason = promptReason(decision === 'approve' ? 'Proposal genehmigen' : 'Proposal ablehnen');
    if (reason == null) return;
    try { await api.post(`/admin/ultimate-v2/proposals/${id}/decide`, { decision, reason }); toast.success('OK'); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const generate = async () => {
    const reason = promptReason('Neues Proposal aus der aktuellen Evidenz erzeugen');
    if (reason == null) return;
    try { const r = await api.post('/admin/ultimate-v2/proposals/generate', { kind: 'market', reason }); if (r.data.skipped) toast(`Kein Proposal: ${r.data.reason}${r.data.failed ? ' (' + r.data.failed.join(', ') + ')' : ''}`); else toast.success('Proposal erzeugt'); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!data) return <Card><SkeletonLine /></Card>;
  // R4R3-C: mgr/canOwner sind jetzt Props vom Top-Level (status-aware, fail-closed).
  return (
    <div className="uv2-learning-grid">
      <Card className="uv2-card uv2-learning-intro">
        <SectionLabel>Learning — nur Vorschläge, KEIN Auto-Apply</SectionLabel>
        <div style={{ fontSize: 12, color: C.muted }}>
          Learning erzeugt ausschließlich unveränderliche Proposals. Der Owner genehmigt, lehnt ab, wendet an (→ neue Config-Revision, CAS + 14 Tage Cooldown) oder rollt zurück.
        </div>
        {/* R2-G: Der Weg von der Evidenz zur scharfen Config — überall identisch erklärt. */}
        <div style={{ fontSize: 13, color: C.muted, marginTop: 8, padding: 8, background: C.brandTint, borderRadius: R.md }}>
          <b>Ablauf:</b> Learning <b>shadow</b> erzeugt ausschließlich Vorschläge · Owner prüft und genehmigt · Learning <b>live</b> erlaubt den manuellen Apply · Producer erzeugt passende Snapshots.
          <div style={{ marginTop: 4 }}>Für den Config-Wechsel muss der Markt zuvor auf shadow/off stehen; test/live wären während des Snapshot-Wechsels unsicher. Auto-Apply bleibt ausgeschlossen.</div>
        </div>
        {/* R2-G: „technisch bereit" (fließt Evidenz?) und „verwertbare Evidenz vorhanden" sind ZWEI Aussagen. */}
        {data.live_evidence && (
          <div style={{ ...kv, marginTop: 8 }}>
            <span>V2-Evidenzfluss (technisch bereit)</span>
            <Badge color={data.live_evidence.ready ? '#16a34a' : '#d97706'}>
              {data.live_evidence.ready ? 'aktiv' : `fehlt: ${(data.live_evidence.missing || []).join(', ')}`}
            </Badge>
          </div>
        )}
        {data.live_evidence && (
          <div style={{ fontSize: 12, color: C.faint, marginBottom: 2 }}>
            Modus {data.live_evidence.market_mode} · Telemetrie {data.live_evidence.telemetry_collect ? 'collect' : 'aus'} · Ledger-Schlüssel {data.live_evidence.ledger_key_ready ? 'bereit' : 'fehlt'}
          </div>
        )}
        {data.evidence_usable && (
          <div style={{ ...kv, borderBottom: 'none' }}>
            <span>Verwertbare Evidenz vorhanden</span>
            <Badge color={data.evidence_usable.ok ? '#16a34a' : C.muted}>
              {data.evidence_usable.ok ? 'ja — Gates erfüllt' : `nein (offen: ${(data.evidence_usable.failed || []).join(', ') || '—'})`}
            </Badge>
          </div>
        )}
        {data.evidence_usable && (
          <div style={{ fontSize: 12, color: C.faint }}>gemessen: {data.evidence_usable.live_days} Betriebstage · {data.evidence_usable.quotes} Quotes · {data.evidence_usable.customers} Kunden · {data.evidence_usable.bookings} Buchungen</div>
        )}
        <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>Gates: ≥{data.gates.minDays} Tage · ≥{data.gates.minIntents} Intentionen · ≥{data.gates.minQuotes} Quotes · ≥{data.gates.minCustomers} Kunden · ≥{data.gates.minBookings} Buchungen · ≥{Math.round(data.gates.minCompletePct * 100)}% vollständig · max. Schritt {data.gates.maxStep}.</div>
        {mgr && <button onClick={generate} style={{ ...pillBtn(false, C.brand), marginTop: 10 }}>Proposal aus Evidenz erzeugen</button>}
      </Card>
      <Card className="uv2-card uv2-proposals-card" style={{ marginTop: 12 }}>
        {data.proposals.length ? data.proposals.map((p) => {
          let s = {}; try { s = JSON.parse(p.samples || '{}'); } catch (_) {}
          let rb = {}; try { rb = JSON.parse(p.rollback_snapshot || '{}'); } catch (_) {}
          return (
          <div key={p.id} style={{ border: `1px solid ${C.line}`, borderRadius: R.md, padding: 10, marginBottom: 8, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span><b>{p.kind}</b> · <Badge color={p.status === 'applied' ? '#16a34a' : p.status === 'approved' ? '#2563eb' : p.status === 'rejected' ? '#ef4444' : p.status === 'rolled_back' ? '#d97706' : C.muted}>{p.status}</Badge></span>
              <span style={{ color: C.faint }}>{p.created_at}</span>
            </div>
            <div style={{ color: C.muted, marginTop: 4 }}>Fenster {p.window_from?.slice(0, 10)} – {p.window_to?.slice(0, 10)}</div>
            {/* G4: alle relevanten Proposal-Felder zeigen */}
            <Grid pairs={[
              ['Intentionen', s.intents], ['Kunden', s.customers], ['Buchungen', s.bookings], ['Quotes', s.quotes],
              ['Live-Tage', s.liveDays], ['Vollständigkeit', s.completePct != null ? Math.round(s.completePct * 100) + '%' : '—'],
              ['Richtung', p.direction], ['Confidence', fmt(p.confidence)], ['Modell', p.model_version],
              ['Config-Hash', p.config_hash ? String(p.config_hash).slice(0, 10) : '—'], ['Daten-Hash', p.data_hash ? String(p.data_hash).slice(0, 10) : '—'],
              ['Evidence-Hash', p.evidence_hash ? String(p.evidence_hash).slice(0, 12) : '—'], ['Basis-Revision', p.base_revision ? String(p.base_revision).slice(0, 8) : 'defaults'],
              ['Angewandte Revision', p.applied_revision ? String(p.applied_revision).slice(0, 8) : '—'], ['Rollback-Ziel', rb.revisionId ? String(rb.revisionId).slice(0, 8) : 'defaults'],
              ['Cooldown bis', p.cooldown_until || '—'], ['Entscheider', p.decided_by || '—'], ['Entschieden', p.decided_at || '—'],
            ]} />
            <div style={{ marginTop: 4, fontSize: 12 }}>Änderungen (erwarteter Effekt): <span style={{ color: C.brand }}>{p.changes}</span></div>
            {(mgr || canApplyProposals) && <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {canApplyProposals && p.status === 'proposed' && <><button onClick={() => decide(p.id, 'approve')} style={pillBtn(false, '#2563eb')}>Genehmigen</button><button onClick={() => decide(p.id, 'reject')} style={pillBtn(false, '#ef4444')}>Ablehnen</button></>}
              {/* R4R3-D: Apply erzeugt eine neue Live-Config-Revision, Rollback rollt sie zurück → beide Owner-only. */}
              {canApplyProposals && p.status === 'approved' && <button onClick={() => act(p.id, 'apply', 'Proposal ANWENDEN (neue Config-Revision, Owner-only)')} style={pillBtn(false, '#16a34a')}>Anwenden</button>}
              {canApplyProposals && p.status === 'applied' && <button onClick={() => act(p.id, 'rollback', 'Angewandtes Proposal zurückrollen')} style={pillBtn(false, '#d97706')}>Rollback</button>}
            </div>}
          </div>
          );
        }) : <div style={{ fontSize: 13, color: C.muted }}>Keine Proposals.</div>}
      </Card>
    </div>
  );
}

function SkeletonLine() { return <div style={{ height: 14, background: C.line, borderRadius: 6, width: '60%' }} />; }
